import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, forwardRef } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import {
  articleSalienceAlpha,
  cloneForceGraphData,
  ENTITY_TYPE_COLORS,
  FOCUS_RING_COLOR,
  linkDistanceForWeight,
  orderStoryNodesForPaint,
  storyArticleSalienceRange,
} from '../services/graphApi';

const RING_RADIUS = 100;
const ARTICLE_RING = 40;
const CONSTELLATION_RING = 90;
const CONSTELLATION_SEED_RING = 48;
const DIM_NODE_ALPHA = 0.18;
const DIM_LINK_ALPHA = 0.08;
const FONT_SEED = 5.2;
const FONT_NODE = 4.4;
const PAD_X = 4.5;
const PAD_Y = 3.2;
const MAX_LABEL_WIDTH = 72;
const MAX_LINES = 4;
const MIN_BOX_W = 28;
const MIN_BOX_H = 16;

function endpointId(endpoint) {
  if (endpoint == null) {
    return null;
  }
  return String(typeof endpoint === 'object' ? endpoint.id : endpoint);
}

function wrapTitle(ctx, text, maxWidth, maxLines = 3) {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return [''];
  }
  const lines = [];
  let line = words[0];
  for (let i = 1; i < words.length; i += 1) {
    const next = `${line} ${words[i]}`;
    if (ctx.measureText(next).width <= maxWidth) {
      line = next;
    } else {
      lines.push(line);
      line = words[i];
      if (lines.length >= maxLines - 1) {
        lines.push([line, ...words.slice(i + 1)].join(' '));
        return lines;
      }
    }
  }
  lines.push(line);
  return lines;
}

function fontSizeForEntityBlock(node, isSeed) {
  const base = isSeed ? FONT_SEED : FONT_NODE;
  const bump = Math.min(Math.sqrt(Math.max(node.articleCount || 1, 1)) * 0.25, 1.4);
  return base + bump;
}

function wrapEntityLabel(ctx, text, maxWidth) {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return [''];
  }
  const lines = [];
  let line = words[0];
  for (let i = 1; i < words.length; i += 1) {
    const next = `${line} ${words[i]}`;
    if (ctx.measureText(next).width <= maxWidth) {
      line = next;
    } else {
      lines.push(line);
      line = words[i];
      if (lines.length >= MAX_LINES - 1) {
        lines.push([line, ...words.slice(i + 1)].join(' '));
        return lines;
      }
    }
  }
  lines.push(line);
  return lines;
}

function measureEntityBlock(ctx, node, { isSeed = false } = {}) {
  const fontSize = fontSizeForEntityBlock(node, isSeed);
  ctx.font = `600 ${fontSize}px "IBM Plex Sans", system-ui, sans-serif`;
  const lines = wrapEntityLabel(ctx, node.name || '', MAX_LABEL_WIDTH);
  let textW = 0;
  lines.forEach((lineLine) => {
    textW = Math.max(textW, ctx.measureText(lineLine).width);
  });
  const lineH = fontSize * 1.2;
  const padX = isSeed ? PAD_X + 1.5 : PAD_X;
  const padY = isSeed ? PAD_Y + 1 : PAD_Y;
  const w = Math.max(MIN_BOX_W, textW + padX * 2);
  const h = Math.max(MIN_BOX_H, lines.length * lineH + padY * 2);
  return { fontSize, lines, lineH, w, h, radius: Math.min(h * 0.28, 5) };
}

function seedStoryEntityRingPositions(nodes, seedKeySet, seedRing = CONSTELLATION_SEED_RING) {
  const seeds = (nodes || []).filter(
    (node) => node.nodeKind === 'entity'
      && (seedKeySet.has(String(node.id)) || (node.hop ?? 0) === 0),
  );
  seeds.forEach((node, index) => {
    const angle = (2 * Math.PI * index) / Math.max(seeds.length, 1);
    node.x = Math.cos(angle) * seedRing;
    node.y = Math.sin(angle) * seedRing;
  });
  return seeds.length;
}

function createStoryRadialForce({ ringRadius = RING_RADIUS, articleRing = ARTICLE_RING, strength = 0.3 } = {}) {
  let nodes = [];
  function force(alpha) {
    const k = strength * alpha;
    for (let i = 0; i < nodes.length; i += 1) {
      const node = nodes[i];
      const isArticle = node.nodeKind === 'article';
      const hop = node.hop ?? 1;
      const targetR = isArticle ? articleRing : Math.max(hop, 1) * ringRadius;
      const dx = node.x ?? 0;
      const dy = node.y ?? 0;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1e-6;
      const pull = ((targetR - dist) / dist) * k;
      node.vx = (node.vx ?? 0) + dx * pull;
      node.vy = (node.vy ?? 0) + dy * pull;
    }
  }
  force.initialize = (input) => {
    nodes = input;
  };
  return force;
}

function createStoryConstellationRadialForce({
  ringRadius = CONSTELLATION_RING,
  seedRing = CONSTELLATION_SEED_RING,
  articleRing = ARTICLE_RING,
  strength = 0.32,
} = {}) {
  let nodes = [];
  function force(alpha) {
    const k = strength * alpha;
    for (let i = 0; i < nodes.length; i += 1) {
      const node = nodes[i];
      let targetR;
      if (node.nodeKind === 'article') {
        targetR = articleRing;
      } else {
        const hop = node.hop ?? 1;
        targetR = hop === 0 ? seedRing : hop * ringRadius;
      }
      const dx = node.x ?? 0;
      const dy = node.y ?? 0;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1e-6;
      const pull = ((targetR - dist) / dist) * k;
      node.vx = (node.vx ?? 0) + dx * pull;
      node.vy = (node.vy ?? 0) + dy * pull;
    }
  }
  force.initialize = (input) => {
    nodes = input;
  };
  return force;
}

function configureForces(graphRef, layout = 'starburst') {
  const fg = graphRef.current;
  if (!fg) {
    return;
  }
  const linkForce = fg.d3Force('link');
  if (linkForce) {
    linkForce.distance((link) => linkDistanceForWeight(link.weight, {
      base: layout === 'constellation' ? 46 : 52,
      min: layout === 'constellation' ? 18 : 28,
    }));
  }
  const chargeForce = fg.d3Force('charge');
  if (chargeForce) {
    chargeForce.strength(layout === 'constellation' ? -110 : -140);
    if (typeof chargeForce.distanceMax === 'function') {
      chargeForce.distanceMax(
        (layout === 'constellation' ? CONSTELLATION_RING : RING_RADIUS) * (layout === 'constellation' ? 4 : 5),
      );
    }
  }
  if (layout === 'constellation') {
    fg.d3Force('radial', createStoryConstellationRadialForce());
  } else {
    fg.d3Force('radial', createStoryRadialForce());
  }
  fg.d3ReheatSimulation?.();
}

/**
 * Bipartite Story canvas (MS-VIZ-04) — article title blocks + entity nodes.
 * Used for both Starburst and Constellation when view=story.
 */
const MemeticStoryCanvas = forwardRef(({
  graphData,
  onNodeClick,
  reducedMotion = false,
  layoutKey = 0,
  focusNodeIds = null,
  seedIds = [],
  layout = 'starburst',
}, ref) => {
  const graphRef = useRef(null);
  const hostRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [highlightNodes, setHighlightNodes] = useState(null);

  /** External focus (e.g. Coverage timeline hover) reuses the canvas hover dim treatment. */
  const focusHighlight = useMemo(() => {
    const keys = (focusNodeIds || []).map((id) => String(id)).filter(Boolean);
    return keys.length > 0 ? new Set(keys) : null;
  }, [focusNodeIds]);

  const seedKeySet = useMemo(
    () => new Set((seedIds || []).filter(Boolean).map((id) => String(id))),
    [seedIds],
  );
  const isConstellationLayout = layout === 'constellation';

  const activeHighlight = highlightNodes ?? focusHighlight;

  const data = useMemo(() => {
    const cloned = cloneForceGraphData(graphData);
    // Preserve nodeKind / articleId after clone (clone strips unknown fields).
    const kindById = new Map();
    (graphData?.nodes || []).forEach((node) => {
      kindById.set(String(node.id), {
        nodeKind: node.nodeKind || (String(node.type || '').toUpperCase() === 'ARTICLE' ? 'article' : 'entity'),
        articleId: node.articleId || null,
        publishedAt: node.publishedAt ?? 0,
      });
    });
    cloned.nodes.forEach((node) => {
      const meta = kindById.get(String(node.id));
      if (meta) {
        node.nodeKind = meta.nodeKind;
        node.articleId = meta.articleId;
        node.publishedAt = meta.publishedAt;
      }
    });
    cloned.nodes = orderStoryNodesForPaint(cloned.nodes);
    return cloned;
  }, [graphData]);

  const articleSalienceRange = useMemo(
    () => storyArticleSalienceRange(data.nodes),
    [data.nodes],
  );

  const articleRankById = useMemo(() => {
    const map = new Map();
    const articles = data.nodes.filter((node) => node.nodeKind === 'article');
    articles.forEach((node, index) => {
      map.set(String(node.id), { rankIndex: index, rankCount: articles.length });
    });
    return map;
  }, [data.nodes]);

  const adjacency = useMemo(() => {
    const map = new Map();
    data.nodes.forEach((node) => map.set(String(node.id), new Set()));
    data.links.forEach((link) => {
      const s = endpointId(link.source);
      const t = endpointId(link.target);
      if (map.has(s) && map.has(t)) {
        map.get(s).add(t);
        map.get(t).add(s);
      }
    });
    return map;
  }, [data]);

  const buildNeighborhood = useCallback((...ids) => {
    const set = new Set();
    ids.filter(Boolean).forEach((id) => {
      const key = String(id);
      set.add(key);
      (adjacency.get(key) || []).forEach((neighbor) => set.add(neighbor));
    });
    return set.size > 0 ? set : null;
  }, [adjacency]);

  const handleNodeHover = useCallback((node) => {
    if (!node) {
      setHighlightNodes(null);
      return;
    }
    setHighlightNodes(buildNeighborhood(node.id));
  }, [buildNeighborhood]);

  const graphKey = useMemo(
    () => `${layoutKey}:${layout}:${data.nodes.length}:${data.links.length}:story`,
    [layoutKey, layout, data.nodes.length, data.links.length],
  );

  const ready = dimensions.width > 0 && dimensions.height > 0;

  const fitGraph = useCallback(() => {
    if (!graphRef.current || !data.nodes.length) {
      return;
    }
    graphRef.current.zoomToFit(500, 56);
  }, [data.nodes]);

  useImperativeHandle(ref, () => ({ fitGraph }), [fitGraph]);

  useEffect(() => {
    const element = hostRef.current;
    if (!element) {
      return undefined;
    }
    const measure = () => {
      const rect = element.getBoundingClientRect();
      const width = Math.floor(rect.width);
      const height = Math.floor(rect.height);
      if (width > 0 && height > 0) {
        setDimensions((prev) =>
          prev.width === width && prev.height === height ? prev : { width, height },
        );
      }
    };
    measure();
    const raf = requestAnimationFrame(measure);
    const observer = new ResizeObserver(() => measure());
    observer.observe(element);
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [layoutKey, graphKey]);

  useEffect(() => {
    setHighlightNodes(null);
  }, [data]);

  useEffect(() => {
    if (!ready || !data.nodes.length) {
      return undefined;
    }
    const articles = data.nodes.filter((node) => node.nodeKind === 'article');
    articles.forEach((node, index) => {
      const angle = (2 * Math.PI * index) / Math.max(articles.length, 1);
      node.x = Math.cos(angle) * ARTICLE_RING;
      node.y = Math.sin(angle) * ARTICLE_RING;
    });
    if (isConstellationLayout) {
      seedStoryEntityRingPositions(data.nodes, seedKeySet);
    }
    configureForces(graphRef, layout);
    return undefined;
  }, [graphKey, ready, data.nodes, isConstellationLayout, layout, seedKeySet]);

  useEffect(() => {
    if (!ready || !data.nodes.length) {
      return undefined;
    }
    const delays = reducedMotion ? [50, 150] : [200, 800, 1600];
    const timers = delays.map((delay) => setTimeout(() => fitGraph(), delay));
    return () => timers.forEach(clearTimeout);
  }, [graphKey, ready, data.nodes.length, fitGraph, reducedMotion]);

  const handleEngineStop = useCallback(() => {
    if (data.nodes.length > 0) {
      fitGraph();
    }
  }, [data.nodes.length, fitGraph]);

  const paintNode = useCallback((node, ctx, globalScale) => {
    if (node.x == null || node.y == null) {
      return;
    }
    const dimmed = activeHighlight != null && !activeHighlight.has(String(node.id));
    const focused = focusHighlight != null && focusHighlight.has(String(node.id));
    const isArticle = node.nodeKind === 'article';
    // Hover / timeline highlight: highlighted articles go full opacity; leave → salience alpha.
    let nodeAlpha = 1;
    if (dimmed) {
      nodeAlpha = DIM_NODE_ALPHA;
    } else if (isArticle && activeHighlight == null) {
      const rank = articleRankById.get(String(node.id));
      nodeAlpha = articleSalienceAlpha(
        node.salience ?? 0,
        articleSalienceRange.min,
        articleSalienceRange.max,
        {
          rankIndex: rank?.rankIndex ?? 0,
          rankCount: rank?.rankCount ?? 1,
        },
      );
    }
    ctx.globalAlpha = nodeAlpha;

    if (isArticle) {
      const fontSize = 4.2;
      ctx.font = `600 ${fontSize}px "IBM Plex Sans", system-ui, sans-serif`;
      const lines = wrapTitle(ctx, node.name || '', 70, 3);
      const lineH = fontSize * 1.2;
      let textW = 0;
      lines.forEach((line) => {
        textW = Math.max(textW, ctx.measureText(line).width);
      });
      const w = Math.max(36, textW + 8);
      const h = Math.max(16, lines.length * lineH + 6);
      const radius = 4;
      node.__boxW = w;
      node.__boxH = h;
      const x = node.x - w / 2;
      const y = node.y - h / 2;
      ctx.fillStyle = '#e8edf2';
      ctx.beginPath();
      ctx.moveTo(x + radius, y);
      ctx.arcTo(x + w, y, x + w, y + h, radius);
      ctx.arcTo(x + w, y + h, x, y + h, radius);
      ctx.arcTo(x, y + h, x, y, radius);
      ctx.arcTo(x, y, x + w, y, radius);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = focused ? FOCUS_RING_COLOR : '#7dd3fc';
      ctx.lineWidth = focused ? 2.4 : 1.5;
      ctx.stroke();
      if (!dimmed) {
        ctx.fillStyle = '#0b1220';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const startY = node.y - ((lines.length - 1) * lineH) / 2;
        lines.forEach((line, i) => {
          ctx.fillText(line, node.x, startY + i * lineH);
        });
      }
    } else if (isConstellationLayout) {
      const isSeed = seedKeySet.has(String(node.id)) || (node.hop ?? 0) === 0;
      const { fontSize, lines, lineH, w, h, radius } = measureEntityBlock(ctx, node, { isSeed });
      const color = ENTITY_TYPE_COLORS[node.type] || '#9aa5b1';
      node.__boxW = w;
      node.__boxH = h;
      ctx.globalAlpha = dimmed ? DIM_NODE_ALPHA : nodeAlpha;
      ctx.fillStyle = color;
      ctx.beginPath();
      const x = node.x - w / 2;
      const y = node.y - h / 2;
      ctx.moveTo(x + radius, y);
      ctx.arcTo(x + w, y, x + w, y + h, radius);
      ctx.arcTo(x + w, y + h, x, y + h, radius);
      ctx.arcTo(x, y + h, x, y, radius);
      ctx.arcTo(x, y, x + w, y, radius);
      ctx.closePath();
      ctx.fill();
      if (isSeed) {
        ctx.strokeStyle = '#7dd3fc';
        ctx.lineWidth = 2.6;
        ctx.stroke();
        ctx.strokeStyle = 'rgba(125, 211, 252, 0.55)';
        ctx.lineWidth = 4;
        ctx.beginPath();
        const hx = node.x - (w + 4) / 2;
        const hy = node.y - (h + 4) / 2;
        const hr = Math.min((h + 4) * 0.28, 6);
        ctx.moveTo(hx + hr, hy);
        ctx.arcTo(hx + w + 4, hy, hx + w + 4, hy + h + 4, hr);
        ctx.arcTo(hx + w + 4, hy + h + 4, hx, hy + h + 4, hr);
        ctx.arcTo(hx, hy + h + 4, hx, hy, hr);
        ctx.arcTo(hx, hy, hx + w + 4, hy, hr);
        ctx.closePath();
        ctx.stroke();
      }
      if (focused) {
        ctx.strokeStyle = FOCUS_RING_COLOR;
        ctx.lineWidth = 2.4;
        ctx.beginPath();
        const fx = node.x - (w + 6) / 2;
        const fy = node.y - (h + 6) / 2;
        const fr = Math.min((h + 6) * 0.28, 6);
        ctx.moveTo(fx + fr, fy);
        ctx.arcTo(fx + w + 6, fy, fx + w + 6, fy + h + 6, fr);
        ctx.arcTo(fx + w + 6, fy + h + 6, fx, fy + h + 6, fr);
        ctx.arcTo(fx, fy + h + 6, fx, fy, fr);
        ctx.arcTo(fx, fy, fx + w + 6, fy, fr);
        ctx.closePath();
        ctx.stroke();
      }
      if (!dimmed) {
        ctx.font = `600 ${fontSize}px "IBM Plex Sans", system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#0b1220';
        const startY = node.y - ((lines.length - 1) * lineH) / 2;
        lines.forEach((line, i) => {
          ctx.fillText(line, node.x, startY + i * lineH);
        });
      }
    } else {
      const isSeed = seedKeySet.has(String(node.id)) || (node.hop ?? 0) === 0;
      let baseRadius = Math.min(5 + Math.sqrt(Math.max(node.articleCount || 1, 1)), 10);
      if (isSeed && globalScale < 0.6) {
        baseRadius = Math.max(baseRadius, 12);
      }
      const radius = baseRadius / Math.max(globalScale, 0.5);
      node.__hitR = radius + 4 / Math.max(globalScale, 0.5);
      const color = ENTITY_TYPE_COLORS[node.type] || '#9aa5b1';
      ctx.beginPath();
      ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI, false);
      ctx.fillStyle = color;
      ctx.fill();
      if (isSeed) {
        ctx.strokeStyle = '#50ff6a';
        ctx.lineWidth = 2.8 / Math.max(globalScale, 0.5);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(node.x, node.y, radius + 4 / Math.max(globalScale, 0.5), 0, 2 * Math.PI, false);
        ctx.strokeStyle = 'rgba(80, 255, 106, 0.45)';
        ctx.lineWidth = 1.5 / Math.max(globalScale, 0.5);
        ctx.stroke();
      }
      if (focused) {
        ctx.beginPath();
        ctx.arc(node.x, node.y, radius + 3.5 / Math.max(globalScale, 0.5), 0, 2 * Math.PI, false);
        ctx.strokeStyle = FOCUS_RING_COLOR;
        ctx.lineWidth = 2 / Math.max(globalScale, 0.5);
        ctx.stroke();
      }
      if (!dimmed && (isSeed || globalScale > 0.85)) {
        const label = node.name || '';
        const fontSize = isSeed
          ? Math.max(12 / globalScale, 3.5)
          : Math.max(9 / globalScale, 3);
        ctx.font = `${isSeed ? 600 : 400} ${fontSize}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillStyle = '#e8edf2';
        const maxLen = isSeed ? 24 : 22;
        const sliceLen = maxLen - 2;
        ctx.fillText(
          label.length > maxLen ? `${label.slice(0, sliceLen)}…` : label,
          node.x,
          node.y + radius + 2,
        );
      }
    }
    ctx.globalAlpha = 1;
  }, [activeHighlight, focusHighlight, articleSalienceRange, articleRankById, isConstellationLayout, seedKeySet]);

  const paintNodePointerArea = useCallback((node, color, ctx) => {
    if (node.x == null || node.y == null) {
      return;
    }
    if (node.nodeKind === 'article' || (isConstellationLayout && node.__boxW && node.__boxH)) {
      const w = node.__boxW || 40;
      const h = node.__boxH || 18;
      ctx.fillStyle = color;
      const pad = node.nodeKind === 'article' ? 0 : 5;
      ctx.fillRect(node.x - w / 2 - pad, node.y - h / 2 - pad, w + pad * 2, h + pad * 2);
      return;
    }
    ctx.fillStyle = color;
    ctx.beginPath();
    const hitR = node.__hitR || 10;
    ctx.arc(node.x, node.y, hitR, 0, 2 * Math.PI, false);
    ctx.fill();
  }, [isConstellationLayout]);

  const paintLink = useCallback((link, ctx, globalScale) => {
    const source = link.source;
    const target = link.target;
    if (source?.x == null || source?.y == null || target?.x == null || target?.y == null) {
      return;
    }
    const active = activeHighlight == null
      || (activeHighlight.has(endpointId(source)) && activeHighlight.has(endpointId(target)));
    ctx.globalAlpha = active ? 1 : DIM_LINK_ALPHA;
    ctx.strokeStyle = 'rgba(125, 211, 252, 0.45)';
    ctx.lineWidth = 1.1 / Math.max(globalScale, 0.4);
    ctx.beginPath();
    ctx.moveTo(source.x, source.y);
    ctx.lineTo(target.x, target.y);
    ctx.stroke();
    ctx.globalAlpha = 1;
  }, [activeHighlight]);

  if (!data.nodes.length) {
    return (
      <div className="memetic-canvas-empty">
        No story nodes in this window — try widening Density or the time range.
      </div>
    );
  }

  return (
    <div className="memetic-force-graph-host" ref={hostRef}>
      {ready && (
        <ForceGraph2D
          key={graphKey}
          ref={graphRef}
          graphData={data}
          width={dimensions.width}
          height={dimensions.height}
          backgroundColor="transparent"
          nodeRelSize={1}
          linkDirectionalParticles={0}
          cooldownTicks={reducedMotion ? 0 : 120}
          warmupTicks={reducedMotion ? 0 : 40}
          enableNodeDrag={!reducedMotion}
          enablePanInteraction
          enableZoomInteraction
          onEngineStop={handleEngineStop}
          onNodeClick={(node, event) => onNodeClick?.(node, event)}
          onNodeHover={handleNodeHover}
          nodeCanvasObject={paintNode}
          nodeCanvasObjectMode={() => 'replace'}
          nodePointerAreaPaint={paintNodePointerArea}
          linkCanvasObject={paintLink}
          linkCanvasObjectMode={() => 'replace'}
        />
      )}
    </div>
  );
});

MemeticStoryCanvas.displayName = 'MemeticStoryCanvas';

export default MemeticStoryCanvas;
